/**
 * Custom hook for lip sync functionality in AnimationStudio
 * Handles lip sync generation, preview sync, and timeline controls
 */
import { useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../../utils/studioUtils';

export function useLipsync(sceneId) {
  // Lip sync generation state
  const [applyingLipSync, setApplyingLipSync] = useState(false);
  const [lipsyncTakes, setLipsyncTakes] = useState([]);
  const [activeLipsyncIndex, setActiveLipsyncIndex] = useState(0);
  
  // Timeline controls
  const [audioOffset, setAudioOffset] = useState(0);
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(null);
  const [isDraggingTrim, setIsDraggingTrim] = useState(null);
  
  // Audio/video info
  const [audioDuration, setAudioDuration] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [audioWaveform, setAudioWaveform] = useState([]);
  
  // Voice selection
  const [selectedVoiceForMerge, setSelectedVoiceForMerge] = useState(null);
  const [lipSyncVideoUrl, setLipSyncVideoUrl] = useState(null);
  
  // Preview sync
  const [syncPreviewUrl, setSyncPreviewUrl] = useState(null);
  const [generatingSyncPreview, setGeneratingSyncPreview] = useState(false);
  
  // Playback state
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);
  const [playheadPosition, setPlayheadPosition] = useState(0);

  const pollingRef = useRef(null);
  const cancelRef = useRef(false);

  // Initialize from scene data
  const initializeFromScene = useCallback((sceneData) => {
    if (!sceneData) return;
    
    setLipsyncTakes(sceneData.lipsync_takes || []);
    setActiveLipsyncIndex(sceneData.active_lipsync_index || 0);
    
    // Timeline state from scene
    if (sceneData.lipsync_audio_offset !== undefined) setAudioOffset(sceneData.lipsync_audio_offset);
    if (sceneData.lipsync_trim_start !== undefined) setTrimStart(sceneData.lipsync_trim_start);
    if (sceneData.lipsync_trim_end !== undefined) setTrimEnd(sceneData.lipsync_trim_end);
    if (sceneData.lipsync_selected_voice_id) setSelectedVoiceForMerge(sceneData.lipsync_selected_voice_id);
    if (sceneData.lipsync_video_url) setLipSyncVideoUrl(sceneData.lipsync_video_url);
  }, []);

  // Apply lip sync
  const applyLipSync = useCallback(async (params, onComplete) => {
    const {
      voiceAssetId,
      characterAssetId,
      videoUrl,
      isUploadedVideo
    } = params;

    if (!voiceAssetId) {
      toast.error('Please select a voice track');
      return;
    }

    cancelRef.current = false;
    setApplyingLipSync(true);

    try {
      const response = await axios.post(`${API}/scenes/${sceneId}/lipsync`, {
        voice_asset_id: voiceAssetId,
        character_asset_id: characterAssetId,
        audio_offset: audioOffset,
        trim_start: trimStart,
        trim_end: trimEnd,
        video_url: videoUrl,
        is_uploaded_video: isUploadedVideo
      });

      if (response.data.success) {
        // Poll for completion
        const pollLipSync = async () => {
          if (cancelRef.current) {
            setApplyingLipSync(false);
            return;
          }

          try {
            const statusResponse = await axios.get(`${API}/scenes/${sceneId}/lipsync-status`);
            const { status, lipsync_video_url, take, error } = statusResponse.data;

            if (status === 'completed' && lipsync_video_url) {
              setApplyingLipSync(false);
              setLipSyncVideoUrl(lipsync_video_url);
              
              // Refresh takes
              const sceneResponse = await axios.get(`${API}/scenes/${sceneId}`);
              const takes = sceneResponse.data.lipsync_takes || [];
              setLipsyncTakes(takes);
              setActiveLipsyncIndex(Math.max(0, takes.length - 1));
              
              toast.success('Lip sync complete!');
              onComplete?.(take || takes[takes.length - 1]);
              return;
            }

            if (status === 'failed' || status === 'error') {
              setApplyingLipSync(false);
              toast.error(error || 'Lip sync failed');
              return;
            }

            pollingRef.current = setTimeout(pollLipSync, 3000);
          } catch (error) {
            console.error('Lip sync polling error:', error);
            pollingRef.current = setTimeout(pollLipSync, 5000);
          }
        };

        pollLipSync();
      } else {
        throw new Error(response.data.error || 'Lip sync failed');
      }
    } catch (error) {
      setApplyingLipSync(false);
      console.error('Lip sync error:', error);
      toast.error(error.response?.data?.detail || 'Failed to apply lip sync');
    }
  }, [sceneId, audioOffset, trimStart, trimEnd]);

  // Generate preview sync
  const generateSyncPreview = useCallback(async (params) => {
    const { videoUrl, voiceAssetId, audioUrl } = params;

    if (!videoUrl) {
      toast.error('No video available for preview');
      return;
    }

    if (!voiceAssetId && !audioUrl) {
      toast.error('Please select a voice track');
      return;
    }

    setGeneratingSyncPreview(true);
    setSyncPreviewUrl(null);

    try {
      const response = await axios.post(`${API}/scenes/${sceneId}/preview-sync`, {
        video_url: videoUrl,
        voice_asset_id: voiceAssetId,
        audio_url: audioUrl,
        audio_offset: audioOffset,
        trim_start: trimStart,
        trim_end: trimEnd
      });

      if (response.data.success && response.data.preview_url) {
        setSyncPreviewUrl(response.data.preview_url);
        toast.success('Preview generated');
        return response.data.preview_url;
      } else {
        throw new Error(response.data.error || 'Preview generation failed');
      }
    } catch (error) {
      console.error('Preview sync error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate preview');
      return null;
    } finally {
      setGeneratingSyncPreview(false);
    }
  }, [sceneId, audioOffset, trimStart, trimEnd]);

  // Clear preview sync (when timeline changes)
  const clearSyncPreview = useCallback(() => {
    setSyncPreviewUrl(null);
  }, []);

  // Cancel lip sync
  const cancelLipSync = useCallback(() => {
    cancelRef.current = true;
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
    }
    setApplyingLipSync(false);
  }, []);

  // Delete a lip sync take
  const deleteLipsyncTake = useCallback(async (takeId) => {
    try {
      await axios.delete(`${API}/scenes/${sceneId}/lipsync-takes/${takeId}`);
      setLipsyncTakes(prev => prev.filter(t => t.id !== takeId));
      toast.success('Take deleted');
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete take');
    }
  }, [sceneId]);

  // Cleanup
  const cleanup = useCallback(() => {
    cancelRef.current = true;
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
    }
  }, []);

  return {
    // Lip sync generation
    applyingLipSync,
    setApplyingLipSync,
    lipsyncTakes,
    setLipsyncTakes,
    activeLipsyncIndex,
    setActiveLipsyncIndex,
    
    // Timeline controls
    audioOffset,
    setAudioOffset,
    trimStart,
    setTrimStart,
    trimEnd,
    setTrimEnd,
    isDraggingTrim,
    setIsDraggingTrim,
    
    // Audio/video info
    audioDuration,
    setAudioDuration,
    videoDuration,
    setVideoDuration,
    audioWaveform,
    setAudioWaveform,
    
    // Voice selection
    selectedVoiceForMerge,
    setSelectedVoiceForMerge,
    lipSyncVideoUrl,
    setLipSyncVideoUrl,
    
    // Preview sync
    syncPreviewUrl,
    setSyncPreviewUrl,
    generatingSyncPreview,
    setGeneratingSyncPreview,
    
    // Playback
    isPreviewPlaying,
    setIsPreviewPlaying,
    playheadPosition,
    setPlayheadPosition,
    
    // Methods
    initializeFromScene,
    applyLipSync,
    generateSyncPreview,
    clearSyncPreview,
    cancelLipSync,
    deleteLipsyncTake,
    cleanup
  };
}

export default useLipsync;
